/**
 * WebSocket Chat Client for Adieuu
 *
 * Handles real-time communication with the chat service.
 * Authenticates using identity session cookies.
 */
// ============================================================================
// Chat Client
// ============================================================================
export class ChatClient {
    ws = null;
    config;
    events;
    state = 'disconnected';
    heartbeatTimer = null;
    reconnectTimer = null;
    connectTimeoutTimer = null;
    pongTimeoutTimer = null;
    reconnectAttempts = 0;
    intentionalClose = false;
    visibilityHandler = null;
    lastPingSentAt = null;
    lastHeartbeatRttMs = null;
    constructor(config, events = {}) {
        this.config = {
            wsUrl: config.wsUrl,
            authToken: config.authToken ?? '',
            heartbeatInterval: config.heartbeatInterval ?? 15000,
            backgroundHeartbeatInterval: config.backgroundHeartbeatInterval ?? 90000,
            reconnectDelay: config.reconnectDelay ?? 1000,
            maxReconnectAttempts: config.maxReconnectAttempts ?? Infinity,
            connectTimeout: config.connectTimeout ?? 10000,
            pongTimeout: config.pongTimeout ?? 10000,
        };
        this.events = events;
    }
    /**
     * Get the current connection state
     */
    getState() {
        return this.state;
    }
    /**
     * Check if connected
     */
    isConnected() {
        return this.state === 'connected';
    }
    /**
     * Round-trip time of the most recent successful chat heartbeat (ms), or null if none yet.
     */
    getLastHeartbeatRttMs() {
        return this.lastHeartbeatRttMs;
    }
    /**
     * Connect to the chat server
     */
    connect() {
        if (this.ws && (this.state === 'connected' || this.state === 'connecting')) {
            return;
        }
        this.intentionalClose = false;
        this.setState(this.reconnectAttempts > 0 ? 'reconnecting' : 'connecting');
        const url = this.config.wsUrl;
        // Prefer Sec-WebSocket-Protocol over ?token= (avoids session ids in URLs/logs).
        const protocols = this.config.authToken
            ? [`adieuu.session.${this.config.authToken}`]
            : undefined;
        try {
            this.ws = protocols ? new WebSocket(url, protocols) : new WebSocket(url);
        }
        catch (error) {
            this.handleError(error instanceof Error ? error : new Error('Failed to create WebSocket'));
            this.scheduleReconnect();
            return;
        }
        this.startConnectTimeout();
        this.ws.onopen = () => {
            this.clearConnectTimeout();
            this.reconnectAttempts = 0;
            this.setState('connected');
            this.startHeartbeat();
        };
        this.ws.onclose = (event) => {
            this.clearConnectTimeout();
            this.stopHeartbeat();
            this.clearPongTimeout();
            this.ws = null;
            if (!this.intentionalClose) {
                this.handleError(new Error(`Connection closed: ${event.code} ${event.reason}`));
                this.scheduleReconnect();
            }
            else {
                this.setState('disconnected');
            }
        };
        this.ws.onerror = () => {
            this.handleError(new Error('WebSocket error'));
        };
        this.ws.onmessage = (event) => {
            this.handleMessage(event.data);
        };
    }
    /**
     * Disconnect from the chat server
     */
    disconnect() {
        this.intentionalClose = true;
        this.stopHeartbeat();
        this.clearReconnectTimer();
        this.clearConnectTimeout();
        this.clearPongTimeout();
        this.reconnectAttempts = 0;
        if (this.ws) {
            this.ws.close(1000, 'Client disconnect');
            this.ws = null;
        }
        this.setState('disconnected');
    }
    /**
     * Force an immediate reconnection attempt.
     * Tears down any existing socket, resets backoff, and connects fresh.
     * No-op if the client was intentionally disconnected.
     */
    forceReconnect() {
        if (this.intentionalClose) {
            return;
        }
        this.clearReconnectTimer();
        this.clearConnectTimeout();
        this.clearPongTimeout();
        this.stopHeartbeat();
        this.detachAndCloseSocket();
        this.reconnectAttempts = 0;
        this.connect();
    }
    /**
     * Send a message to the server
     */
    send(message) {
        if (!this.ws || this.state !== 'connected') {
            return false;
        }
        try {
            this.ws.send(JSON.stringify(message));
            return true;
        }
        catch {
            return false;
        }
    }
    /**
     * Update event handlers
     */
    setEventHandlers(events) {
        this.events = { ...this.events, ...events };
    }
    /**
     * Update auth token (for token refresh)
     */
    updateAuthToken(token) {
        this.config.authToken = token;
        if (this.state === 'connected') {
            this.disconnect();
            this.connect();
        }
    }
    // --------------------------------------------------------------------------
    // Private Methods
    // --------------------------------------------------------------------------
    setState(state) {
        if (this.state !== state) {
            this.state = state;
            this.events.onStateChange?.(state);
        }
    }
    handleMessage(data) {
        try {
            const text = typeof data === 'string' ? data : new TextDecoder().decode(data);
            const message = JSON.parse(text);
            if (message.type === 'pong') {
                if (this.lastPingSentAt !== null) {
                    const rttMs = Math.max(0, Math.round(performance.now() - this.lastPingSentAt));
                    this.lastPingSentAt = null;
                    this.lastHeartbeatRttMs = rttMs;
                    this.events.onHeartbeatRtt?.(rttMs);
                }
                this.clearPongTimeout();
            }
            this.events.onMessage?.(message);
        }
        catch {
            this.handleError(new Error('Failed to parse message'));
        }
    }
    handleError(error) {
        this.events.onError?.(error);
    }
    startHeartbeat() {
        this.stopHeartbeat();
        const interval = this.isDocumentHidden()
            ? this.config.backgroundHeartbeatInterval
            : this.config.heartbeatInterval;
        this.sendHeartbeatPing();
        this.heartbeatTimer = setInterval(() => {
            this.sendHeartbeatPing();
        }, interval);
        this.startVisibilityTracking();
    }
    stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
        this.stopVisibilityTracking();
    }
    isDocumentHidden() {
        return typeof document !== 'undefined' && document.hidden;
    }
    startVisibilityTracking() {
        if (typeof document === 'undefined')
            return;
        this.stopVisibilityTracking();
        this.visibilityHandler = () => {
            if (this.state !== 'connected')
                return;
            this.clearPongTimeout();
            if (this.heartbeatTimer) {
                clearInterval(this.heartbeatTimer);
            }
            const interval = document.hidden
                ? this.config.backgroundHeartbeatInterval
                : this.config.heartbeatInterval;
            if (!document.hidden) {
                this.sendHeartbeatPing();
            }
            this.heartbeatTimer = setInterval(() => {
                this.sendHeartbeatPing();
            }, interval);
        };
        document.addEventListener('visibilitychange', this.visibilityHandler);
    }
    stopVisibilityTracking() {
        if (this.visibilityHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = null;
        }
    }
    /**
     * Closes the current socket and detaches all event handlers so that
     * stale callbacks from an abandoned socket never interfere with a
     * subsequent connection attempt.
     */
    detachAndCloseSocket() {
        if (!this.ws)
            return;
        const ws = this.ws;
        ws.onopen = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        this.ws = null;
        try {
            ws.close();
        }
        catch { /* best-effort */ }
    }
    // -- Connect timeout ------------------------------------------------------
    startConnectTimeout() {
        this.clearConnectTimeout();
        this.connectTimeoutTimer = setTimeout(() => {
            if (this.ws && this.state !== 'connected') {
                this.handleError(new Error('Connection timed out'));
                this.detachAndCloseSocket();
                this.scheduleReconnect();
            }
        }, this.config.connectTimeout);
    }
    clearConnectTimeout() {
        if (this.connectTimeoutTimer) {
            clearTimeout(this.connectTimeoutTimer);
            this.connectTimeoutTimer = null;
        }
    }
    // -- Heartbeat ping --------------------------------------------------------
    sendHeartbeatPing() {
        if (this.send({ type: 'ping' })) {
            this.lastPingSentAt = performance.now();
            this.startPongTimeout();
        }
    }
    // -- Pong timeout ----------------------------------------------------------
    startPongTimeout() {
        this.clearPongTimeout();
        this.pongTimeoutTimer = setTimeout(() => {
            this.lastPingSentAt = null;
            if (this.ws && this.state === 'connected') {
                this.handleError(new Error('Heartbeat timeout: no pong received'));
                this.detachAndCloseSocket();
                this.stopHeartbeat();
                this.scheduleReconnect();
            }
        }, this.config.pongTimeout);
    }
    clearPongTimeout() {
        if (this.pongTimeoutTimer) {
            clearTimeout(this.pongTimeoutTimer);
            this.pongTimeoutTimer = null;
        }
    }
    // -- Reconnect scheduling --------------------------------------------------
    scheduleReconnect() {
        if (this.intentionalClose) {
            return;
        }
        if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
            this.setState('disconnected');
            this.handleError(new Error('Max reconnect attempts reached'));
            return;
        }
        this.clearReconnectTimer();
        this.setState('reconnecting');
        const delay = Math.min(this.config.reconnectDelay * Math.pow(2, this.reconnectAttempts), 30000);
        this.reconnectTimer = setTimeout(() => {
            this.reconnectAttempts++;
            this.connect();
        }, delay);
    }
    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
}
// ============================================================================
// Factory
// ============================================================================
/**
 * Creates a chat client instance
 */
export function createChatClient(config, events) {
    return new ChatClient(config, events);
}
//# sourceMappingURL=chat-client.js.map